/**
 * SensitivityAnalysisBlock - Display sensitivity analysis factors
 */

import React from 'react'
import { SensitivityAnalysisBlock as SensitivityAnalysisBlockType } from '../../../../lib/cockpit/schemas'

interface SensitivityAnalysisBlockProps {
  data: SensitivityAnalysisBlockType['data']
  blockId: string
  compact?: boolean
}

const SENSITIVITY_COLORS = {
  very_low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  very_high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
}

const SENSITIVITY_BAR_COLORS = {
  very_low: 'bg-gray-400',
  low: 'bg-blue-500',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  very_high: 'bg-red-500'
}

function SensitivityAnalysisBlock({ data, blockId: _blockId, compact }: SensitivityAnalysisBlockProps) {
  const { factors, baseline_state_id } = data

  if (!factors || factors.length === 0) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-sm italic">
        No sensitivity factors available
      </div>
    )
  }

  // Sort by sensitivity level
  const sortedFactors = [...factors].sort((a, b) => {
    const order = { very_high: 0, high: 1, medium: 2, low: 3, very_low: 4 }
    return (order[a.sensitivity || 'medium'] ?? 2) - (order[b.sensitivity || 'medium'] ?? 2)
  })

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {sortedFactors.slice(0, 4).map((factor, idx) => (
          <span
            key={factor.id || idx}
            className={`px-2 py-1 rounded text-xs font-medium ${
              SENSITIVITY_COLORS[factor.sensitivity || 'medium']
            }`}
          >
            {factor.name}
          </span>
        ))}
        {factors.length > 4 && (
          <span className="px-2 py-1 text-xs text-gray-500">+{factors.length - 4} more</span>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {baseline_state_id && (
        <div className="text-xs text-gray-500 dark:text-gray-500">
          Baseline: {baseline_state_id}
        </div>
      )}

      <div className="space-y-3">
        {sortedFactors.map((factor, idx) => {
          const sensitivityLevel = (typeof factor.sensitivity === 'string' ? factor.sensitivity : 'medium') as keyof typeof SENSITIVITY_COLORS
          const sensitivityWidth = {
            very_low: 20,
            low: 40,
            medium: 60,
            high: 80,
            very_high: 100
          }[sensitivityLevel]

          return (
            <div
              key={factor.id || idx}
              className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900 dark:text-white">{factor.name}</span>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    SENSITIVITY_COLORS[sensitivityLevel]
                  }`}
                >
                  {sensitivityLevel.replace('_', ' ')}
                </span>
              </div>

              {/* Sensitivity bar */}
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full transition-all duration-300 ${SENSITIVITY_BAR_COLORS[sensitivityLevel]}`}
                  style={{ width: `${sensitivityWidth}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                {factor.baseline_value !== undefined && (
                  <span>Baseline: {String(factor.baseline_value)}</span>
                )}
                {factor.range_low !== undefined && factor.range_high !== undefined && (
                  <span>Range: {String(factor.range_low)} – {String(factor.range_high)}</span>
                )}
              </div>

              {factor.impact_on_outcome && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {factor.impact_on_outcome}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SensitivityAnalysisBlock

